import {inject, Injectable} from '@angular/core';
import {S3Client} from '../infrastructure/network/S3Client';
import {catchError, concatMap, map, Observable, of} from 'rxjs';
import {ResultDto} from '../dto/ResultDto';
import {ApiClient} from '../infrastructure/network/ApiClient';
import {ImageType} from '../dto/ImageType';
import {GetPresignedUrlRequestDto} from '../dto/request/GetPresignedUrlRequestDto';
import {ResponseDto} from '../dto/response/ResponseDto';
import {ErrorResponseDto} from '../dto/response/ErrorResponseDto';
import {GetPresignedUrlResponseDto} from '../dto/response/GetPresignedUrlResponseDto';
import {ErrorHandler} from './ErrorHandler';
import {CompleteUploadRequestDto} from '../dto/request/CompleteUploadRequestDto';
import {CompleteUploadResponseDto} from '../dto/response/CompleteUploadResponseDto';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ImageService extends ErrorHandler {

  private readonly getPresignedUrl = '/media/images/users/upload';
  private readonly completeUploadUrl = '/media/images/users/upload/complete';

  private s3Client: S3Client = inject(S3Client);
  private apiClient: ApiClient = inject(ApiClient);

  public uploadImage(file: File, imageType: ImageType): Observable<ResultDto<string | undefined>> {
    const fileName = file.name;
    const request = new GetPresignedUrlRequestDto(fileName, imageType, file.type);
    return this.apiClient.post<GetPresignedUrlRequestDto, ResponseDto<GetPresignedUrlResponseDto | ErrorResponseDto>>(this.getPresignedUrl, request)
      .pipe(
        concatMap((response) => {
          if (response.success && response.data) {
            const resp = response.data as GetPresignedUrlResponseDto;
            return this.uploadImageToPresignedUrl(file, resp);
          }
          return of(ResultDto.error('Failed to get presigned URL'));
        }),
        catchError((error) => this.handleError(error))
      );
  }

  private uploadImageToPresignedUrl(file: File, data: GetPresignedUrlResponseDto): Observable<ResultDto<string | undefined>> {
    const url = data.pre_signed_url.replace('https://dymlyprofileimages.fra1.digitaloceanspaces.com', environment.s3url);
    return this.s3Client.uploadImageToPresignedUrl(file, url, data.required_headers)
      .pipe(
        concatMap(success => {
          if (success) {
            return this.completeUploadRequest(data.uuid);
          } else {
            return of(ResultDto.error('Upload failed'));
          }
        })
      );
  }

  private completeUploadRequest(uuid: string):  Observable<ResultDto<string | undefined>> {
    const completeUploadRequestDto = new CompleteUploadRequestDto(uuid);
    return this.apiClient.post<CompleteUploadRequestDto, ResponseDto<CompleteUploadResponseDto | ErrorResponseDto>>(this.completeUploadUrl, completeUploadRequestDto)
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            const data = response.data as CompleteUploadResponseDto;
            return ResultDto.success(data.cdn_url);
          } else {
            return ResultDto.error('Failed to complete upload');
          }
        }),
        catchError((error) => this.handleError(error))
      )
  }

}
